"use client";

import { createContext, useContext, useState, useCallback, ReactNode } from "react";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

interface ProjectRunningStatus {
  is_running: boolean;
  working_agent_ids: string[];
}

interface ProjectRunningContextType {
  // 获取项目的运行状态
  getProjectStatus: (projectId: string) => ProjectRunningStatus;
  // 设置项目运行状态
  setProjectRunning: (projectId: string, isRunning: boolean) => void;
  // 设置 agent 工作状态
  setAgentWorking: (projectId: string, agentId: string, isWorking: boolean) => void;
  // 从后端同步项目状态
  syncProjectStatus: (projectId: string) => Promise<void>;
  // 清空项目状态
  clearProjectStatus: (projectId: string) => void;
  // 获取所有正在运行的项目 ID
  getRunningProjectIds: () => string[];
  // 获取项目中正在工作的 agent ID 集合
  getWorkingAgentIds: (projectId: string) => Set<string>;
}

// 默认状态
const defaultStatus: ProjectRunningStatus = {
  is_running: false,
  working_agent_ids: [],
};

const ProjectRunningContext = createContext<ProjectRunningContextType | undefined>(undefined);

export function ProjectRunningProvider({ children }: { children: ReactNode }) {
  // 存储每个项目的运行状态
  const [projectStatuses, setProjectStatuses] = useState<Record<string, ProjectRunningStatus>>({});

  // 获取项目状态
  const getProjectStatus = useCallback((projectId: string): ProjectRunningStatus => {
    return projectStatuses[projectId] || defaultStatus;
  }, [projectStatuses]);

  // 设置项目运行状态
  const setProjectRunning = useCallback((projectId: string, isRunning: boolean) => {
    setProjectStatuses(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId] || defaultStatus,
        is_running: isRunning,
        // 如果停止运行，清空 working_agent_ids
        working_agent_ids: isRunning ? (prev[projectId]?.working_agent_ids || []) : [],
      }
    }));
  }, []);

  // 设置 agent 工作状态
  const setAgentWorking = useCallback((projectId: string, agentId: string, isWorking: boolean) => {
    setProjectStatuses(prev => {
      const currentStatus = prev[projectId] || defaultStatus;
      const currentAgentIds = new Set(currentStatus.working_agent_ids);

      if (isWorking) {
        currentAgentIds.add(agentId);
      } else {
        currentAgentIds.delete(agentId);
      }

      return {
        ...prev,
        [projectId]: {
          ...currentStatus,
          working_agent_ids: Array.from(currentAgentIds),
        }
      };
    });
  }, []);

  // 从后端同步项目状态
  const syncProjectStatus = useCallback(async (projectId: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/running-status`);
      if (res.ok) {
        const data = await res.json();
        setProjectStatuses(prev => ({
          ...prev,
          [projectId]: {
            is_running: data.is_running,
            working_agent_ids: data.working_agent_ids || [],
          }
        }));
      }
    } catch (error) {
      console.error("Failed to sync project status:", error);
    }
  }, []);

  // 清空项目状态
  const clearProjectStatus = useCallback((projectId: string) => {
    setProjectStatuses(prev => ({
      ...prev,
      [projectId]: defaultStatus,
    }));
  }, []);

  // 获取所有正在运行的项目 ID
  const getRunningProjectIds = useCallback((): string[] => {
    return Object.entries(projectStatuses)
      .filter(([, status]) => status.is_running)
      .map(([projectId]) => projectId);
  }, [projectStatuses]);

  // 获取项目中正在工作的 agent ID 集合
  const getWorkingAgentIds = useCallback((projectId: string): Set<string> => {
    const status = projectStatuses[projectId];
    return new Set(status?.working_agent_ids || []);
  }, [projectStatuses]);

  return (
    <ProjectRunningContext.Provider value={{
      getProjectStatus,
      setProjectRunning,
      setAgentWorking,
      syncProjectStatus,
      clearProjectStatus,
      getRunningProjectIds,
      getWorkingAgentIds,
    }}>
      {children}
    </ProjectRunningContext.Provider>
  );
}

export function useProjectRunning() {
  const context = useContext(ProjectRunningContext);
  if (!context) {
    throw new Error("useProjectRunning must be used within a ProjectRunningProvider");
  }
  return context;
}
